import { useCallback, useEffect, useState } from 'react';
import type { LogbookAttachment } from '../types/logbook';
import { MAX_ADJUNTOS_NOVEDAD } from '../types/logbook';
import {
  getLogbookAttachments,
  uploadLogbookAttachment,
  getLogbookAttachmentDownloadUrl,
} from '../lib/api/endpoints';
import { logbookAttachmentFromDto } from '../lib/api/mappers';
import { comprimirRecibo } from '../utils/receiptImage';

/** Lo que pasó con cada archivo que se intentó subir. */
export interface ResultadoDeSubida {
  subidas: LogbookAttachment[];
  /** Los que no pudieron: se devuelven para poder REINTENTARLOS, no solo para contarlos. */
  fallidas: File[];
}

/**
 * FE-30 — las fotos de una novedad: listarlas, subirlas y abrirlas.
 *
 * Tres diferencias con `useExpenseReceipts`, y ninguna es de estilo:
 *
 * 1. **No hay "vigente".** La lista es cronológica y cada subida AÑADE. Un
 *    accidente son el guardabarros, la placa del otro carro y el croquis; el
 *    recibo de un gasto es un documento que se vuelve a fotografiar.
 * 2. **`subir` es secuencial y no lanza en el primer fallo.** Tres subidas de
 *    10 MiB compitiendo por el mismo enlace en carretera es peor que una
 *    detrás de otra, y un `Promise.all` que rechaza deja al usuario sin saber
 *    cuáles llegaron. Devuelve `{subidas, fallidas}` para que quien llame
 *    pueda ofrecer reintentar **solo lo que falta**.
 * 3. **El tope se conoce aquí** (`MAX_ADJUNTOS_NOVEDAD`): el servidor responde
 *    409 igualmente, pero cortar antes evita subir 10 MiB para que los
 *    rechacen.
 *
 * ⚠️ La URL de descarga se pide AL ABRIR, nunca al listar. Es una capacidad
 * temporal sobre un objeto concreto: una por miniatura sería N+1 y caducaría
 * mientras el usuario mira la galería.
 */
export function useLogbookAttachments(entryId: string | null) {
  const [attachments, setAttachments] = useState<LogbookAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState<{ actual: number; total: number } | null>(null);

  useEffect(() => {
    if (!entryId) {
      setAttachments([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setAttachments([]);
      try {
        const res = await getLogbookAttachments(entryId);
        if (cancelled) return;
        setAttachments(res.items.map(logbookAttachmentFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setAttachments([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando las fotos'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [entryId]);

  /**
   * Sube los archivos UNO A UNO y no se detiene en el primero que falle.
   *
   * `comprimirRecibo` se vuelve a llamar aunque el selector ya haya preparado
   * el archivo: es idempotente, así que no recodifica, y llamarla aquí
   * garantiza que ningún camino futuro suba un `File` en crudo por no
   * acordarse.
   */
  const subir = useCallback(async (files: File[], destino?: string): Promise<ResultadoDeSubida> => {
    const id = destino ?? entryId;
    if (!id) throw new Error('No hay novedad a la que adjuntar la foto.');
    if (files.length === 0) return { subidas: [], fallidas: [] };

    setSubiendo(true);
    const subidas: LogbookAttachment[] = [];
    const fallidas: File[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setProgreso({ actual: i + 1, total: files.length });
        try {
          const listo = await comprimirRecibo(files[i]);
          subidas.push(logbookAttachmentFromDto(await uploadLogbookAttachment(id, listo)));
        } catch {
          // Se guarda el archivo ORIGINAL, no el comprimido: es lo que hay que
          // poder reintentar, y la compresión puede ser justo lo que falló.
          fallidas.push(files[i]);
        }
      }
      if (subidas.length > 0 && id === entryId) {
        setAttachments(prev => [...prev, ...subidas]);
      }
      return { subidas, fallidas };
    } finally {
      setSubiendo(false);
      setProgreso(null);
    }
  }, [entryId]);

  /** Capacidad temporal sobre una foto concreta. */
  const urlDeDescarga = useCallback(async (attachmentId: string): Promise<string> => {
    if (!entryId) throw new Error('No hay novedad de la que abrir la foto.');
    const res = await getLogbookAttachmentDownloadUrl(entryId, attachmentId);
    return res.url;
  }, [entryId]);

  const quedanCupos = MAX_ADJUNTOS_NOVEDAD - attachments.length;

  return { attachments, isLoading, error, subiendo, progreso, subir, urlDeDescarga, quedanCupos };
}
